import React, { useState, useRef, useEffect } from 'react';
import numeral from 'numeral';
import moment from 'moment';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col } from 'react-grid-system';
import { isEqual } from 'lodash';

import ActionButton from 'SharedComponents/action-button';
import ModalDialogV3 from 'SharedComponents/modal-dialog-v3';
import ContactInfoAndVerificationForm from 'SharedComponents/contact-info-with-verification-form';
//import ContactInfoForm from 'SharedComponents/contact-info-form';
import PopoverHover from 'SharedComponents/popover-hover';
import SelectSubscriptionPlan from 'SharedComponents/select-subscription';
import DirectDebitRequestServiceAgreementText from 'SharedComponents/direct-debit-request-service-agreement-text';

import * as RoundUpsActions from 'Flux/round-ups/actions';

import styles from './AboutRoundUps.css';
import log from 'Utils/logger';

// Define default format for numbers in numeral
numeral.defaultFormat('0,0.00');

// Set default format for nulls
numeral.nullFormat('0.00');
numeral.zeroFormat('0.00');

const AboutRoundUpsWrapper = styled.div`${styles}`;


const AboutRoundUps = ({ onError,
                        }) => {

  const roundups = useSelector(state => state.roundups);
  
  console.log ('*Set AboutRoundUps triggered with roundups: ', roundups);
  
  const dispatch = useDispatch();
  
  const user = useSelector(state => state.authentication.user);
  const identity = useSelector(state => state.authentication.identity);


  const [localRoundups , setLocalRoundups] = useState(roundups);

  const [fullName, setFullname] = useState(
    `${identity?.shopper_fname || ''} ${identity?.shopper_lname || ''}`.trim()
  );

  const selectedPlan = useSelector(state =>
    state.subscriptions.availSubscriptionPlans?.find(plan => plan.subscriptionOptionSelected) || null
  );

  const shopperSubscribedPlan = useSelector(state => 
      state.subscriptions.availSubscriptionPlans?.find(plan => plan.shopperSubscribed) || null
  );

  const [modalType, setModalType] = useState(null);
  const [showModalDialogV3, setShowModalDialogV3] = useState(false);
  const [localCompleteContactDetails, setLocalCompleteContactDetails] = useState(true);

  const [updatingCompleteContactDetails, setUpdatingCompleteContactDetails] = useState(false);
  const [updatingAcceptedRoundUpTerms, setUpdatingAcceptedRoundUpTerms] = useState(false);

  useEffect(() => {
    console.log('AboutRoundUps useEffect - mounted');
    console.log('AboutRoundUps useEffect formatted_address: ', identity?.formatted_address);
    return (() => {
      console.log('AboutRoundUps useEffect - unmounted');
    });
  }, []);

  useEffect(() => {
    if (!isEqual(roundups, localRoundups)) {
      console.log('AboutRoundUps useEffect - roundups changed - updating localRoundups to roundups: ', roundups);
      setLocalRoundups(roundups || {});
    }
  }, [roundups]);
  
  useEffect(() => {
    const {
      shopper_fname,
      shopper_lname,
      shopper_dob,
      shopper_pep,
      formatted_address
    } = identity || {};

    const areDetailsComplete =
      shopper_fname && shopper_lname && shopper_dob && formatted_address && shopper_pep !== null;
      setFullname(`${shopper_fname || ''} ${shopper_lname || ''}`.trim());

    setLocalCompleteContactDetails(areDetailsComplete);
  }, [identity]);

  useEffect(() => {
      if (!localCompleteContactDetails) {
        const errorMessage1 = "Please complete all verification details to continue";
        onError(errorMessage1);
      } else if (!localRoundups.confirmedContactDetails) {
        const errorMessage2 = "Please confirm your verification details are correct to continue";
        onError(errorMessage2);
      } else if (shopperSubscribedPlan?.subscriptionAmount === 0) {
        const errorMessage4 = "Round-Ups is not available on your active plan. Please update & ACTIVATE your plan to continue.";
        onError(errorMessage4);
      } else {
        onError("");
      }
  }, [localRoundups, localRoundups.confirmedContactDetails, localCompleteContactDetails, localRoundups.acceptedRoundupsTerms, selectedPlan?.subscriptionAmount, shopperSubscribedPlan?.subscriptionAmount]);


  async function updateAcceptedRoundUpTerms() {
    console.log('updateAcceptedRoundUpTerms triggered with !localRoundups.acceptedRoundupsTerms: ', !localRoundups.acceptedRoundupsTerms);
    setUpdatingAcceptedRoundUpTerms(true);

    try {
      const result = await dispatch(RoundUpsActions.updateAcceptedRoundUpsTerms(!localRoundups.acceptedRoundupsTerms));
      console.log('updateAcceptedRoundUpsTerms using result: ', result);
      dispatch(RoundUpsActions.setAcceptedRoundUpsTerms( result.data.data.acceptedRoundupsTerms ));
    } catch (exception) {
      console.log('updateAcceptedRoundUpTerms exception: ', exception);
      log({
        event: "System-Error",
        'shopper_id': user ? user.shopper_id : 'Not Signed In',
        source: "AboutRoundUps- UpdateAcceptedRoundUpTerms",
        info: exception,
        ...exception,
      });
      onError("Error - please try again");
    } finally {
      setUpdatingAcceptedRoundUpTerms(false);
    }
  }

  async function updateConfirmedContactDetails() {
    console.log('updateConfirmedContactDetails triggered with !localRoundups.confirmedContactDetails: ', !localRoundups.confirmedContactDetails);
    setUpdatingCompleteContactDetails(true);

    try {
      const result = await dispatch(RoundUpsActions.updateConfirmedContactDetails(!localRoundups.confirmedContactDetails));
      console.log('updateConfirmedContactDetails result: ', result);
      dispatch(RoundUpsActions.setConfirmedContactDetails(result.data.data.confirmedContactDetails));
    } catch (exception) {
      console.log('updateConfirmedContactDetails exception: ', exception);
      log({
        event: "System-Error",
        source: "AboutRoundUps-UpdateConfirmedContactDetails",
        'shopper_id': user ? user.shopper_id : '000',
        info: exception,
        ...exception,
      });
      onError("Error - Please try again");
    } finally {
      setUpdatingCompleteContactDetails(false);
      console.log('updateConfirmedContactDetails finally triggered');
    }
  }

  const toggleConfirmedContactDetails = () => {
    console.log('toggleConfirmedContactDetails triggered');
    updateConfirmedContactDetails();
  };

  const toggleAcceptedRoundUpTerms = () => {
    console.log('toggleAcceptedRoundUpTerms triggered');
    console.log('toggleAcceptedROundUpTerms localRoundups.acceptedRoundupsTerms: ', localRoundups.acceptedRoundupsTerms);
    console.log('toggleAcceptedROundUpTerms !localRoundups.acceptedRoundupsTerms: ', !localRoundups.acceptedRoundupsTerms);
    updateAcceptedRoundUpTerms();
  };

  const getModalConfig = () => {
    switch (modalType) {
      case 'verification':
        return {
          title: "Verification Details",
          explainer: "The following details will be verified against your bank records. Please ensure they are correct as Round-Ups can only be activated if there is a match.",
          content: (
            <ContactInfoAndVerificationForm 
              fieldsToHide={['phone', 'email']}
            />
          )
        };
  
      case 'ddrAgreement':
        return {
          title: "Zai Australia Pty Ltd Direct Debit Request Service Agreement",
          explainer: null,
          content: <DirectDebitRequestServiceAgreementText />
        };
  
      default:
        return {};
    }
  };

  const modalConfig = getModalConfig();

  return (
    <AboutRoundUpsWrapper>
     <div>
      <Container fluid className="links-container" style={{ margin: 0, padding: 0, width: '100%' }}>
        <Row style={{ margin: 0, padding: 0, width: '100%' }}>
          <Col xl={6} lg={6} md={6} sm={12} xs={12} className="links-group" style={{ margin: 0, padding: 0 }}>
            <div className="acceptance-card">
              <h2 className="title">1. Select Subscription Plan</h2>
              <SelectSubscriptionPlan
                subscriptionFeaturesOn={true}
              />
            </div>
          </Col>
          <Col xl={6} lg={6} md={6} sm={12} xs={12} className="links-group" style={{ margin: 0, padding: 0 }}>

            <div className="acceptance-card">
              <h2 className="title">2. Verification Details</h2>
              <h3>Verifying Your Identity</h3>
                <p>Under Australian law (AUSTRAC), we are required to verify your identity to comply with anti-money laundering and counter-terrorism financing laws. This is a standard process that helps keep everyone safe and secure. We conduct the verification using the details below.</p>
                <p>The details below must match your bank details in order for us to activate the Round-Ups service.</p>
                <p>There is a once off $2.00 fee for verification. This will be charged AFTER your Round-Ups has been activated.</p>
              <ul>
                <li> {fullName ? <b>Legal Name :&nbsp;</b> : <b style={{color:"red"}}>Legal Name :&nbsp;</b>}{fullName}</li>
                <li> {identity?.shopper_dob ? <b>DOB : &nbsp;</b> : <b style={{color:"red"}}>DOB :&nbsp;</b>}{identity?.shopper_dob ? moment(identity?.shopper_dob).format('DD-MM-YYYY') : ""}</li>
                <li> {identity?.formatted_address ? <b>Address :&nbsp;</b> : <b style={{color:"red"}}>Address :&nbsp;</b>}{identity?.formatted_address}</li>
                <li> {identity?.shopper_pep !== null ? <b>Politically Exposed : &nbsp;</b> : <b style={{color:"red"}}>Politically Exposed ?&nbsp;</b>}  {identity?.shopper_pep === true ? "Yes" : identity?.shopper_pep === false ? "No" : ""}</li>
              </ul>
              <ActionButton
                text="Update Verification Details"
                onClick={() =>  {
                  setModalType('verification');
                  setShowModalDialogV3(true);
                }}
                color="#fff"
                large={true}
              />
              { !localCompleteContactDetails ? null : 
                (
                  <div className="checkbox-entry">
                      <input
                        className="input-checkbox"
                        name="confirmed-contact-details"
                        type="checkbox"
                        placeholder="Confirm contact details"
                        checked={localRoundups.confirmedContactDetails}
                        required
                        onChange={localRoundups.confirmedContactDetails ? null : toggleConfirmedContactDetails}
                        disabled={localRoundups.confirmedContactDetails}
                      />
                      <span>I confirm my verification details above are correct.</span>
                      <span className="pop-over-container">
                          <PopoverHover
                            content={
                              <div style={{
                                maxWidth: '300px', padding: 10, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', textAlign: 'left',
                              }}
                              >
                              These details will be verified against your bank details and must match for us to turn on Round Ups service.
                              </div>
                            }
                            header = {null}
                            img = {null}
                            itemToPopover = {
                              ( <i className="material-icons" style={{ margin: '0 5px 0 5px', verticalAlign: '-40%' }} >info</i> )
                            }
                            anchorOrigin={{
                              vertical: 'center',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'center',
                              horizontal: 'right',
                            }}
                          />
                      </span>
                    </div>
                )
              }
            </div>
          </Col>
          
        </Row>

      </Container>
        </div>
        <div>
          <ModalDialogV3 
            open={showModalDialogV3}
            onCloseModalDialogV3={() => setShowModalDialogV3(false)}
            titleComponent={modalConfig.title}
            explainerComponent={modalConfig.explainer}
            contentComponent={modalConfig.content}
          /> 
      </div>

    </AboutRoundUpsWrapper>
  );
}

export default AboutRoundUps;

